import React, { useState, useEffect, useCallback } from 'react';
import {
  Send, Plus, Edit2, Trash2, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, RefreshCw, ChevronDown, X, Users, User,
  Filter,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { telegramNotifyAPI } from '../../services/api';

const ALL_DAYS = [
  { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 }, { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 }, { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

const MODULE_INFO = {
  homework:            'homework count, due today, overdue, submission %',
  attendance:          'present / late / absent counts and attendance %',
  toddleLessonPlan:    'completed vs pending plans and completion %',
  morningActivity:     'completed / pending / missed counts for today',
  learningTrackers:    'submitted vs pending entries and completion %',
  newsletter:          'published vs draft newsletter counts',
  grievances:          'new / in-progress / reopened grievance counts',
  teacherObservations: 'completed / draft / pending observation counts',
};

// Which condition types each module supports
const MODULE_CONDITIONS = {
  homework:            ['always', 'if_pending', 'if_below_pct'],
  attendance:          ['always', 'if_pending', 'if_below_pct'],
  toddleLessonPlan:    ['always', 'if_pending', 'if_below_pct'],
  morningActivity:     ['always', 'if_pending', 'if_below_pct'],
  learningTrackers:    ['always', 'if_pending', 'if_below_pct'],
  newsletter:          ['always', 'if_pending'],
  grievances:          ['always', 'if_pending'],
  teacherObservations: ['always', 'if_pending', 'if_below_pct'],
};

// Human-readable label for "if_pending" per module
const PENDING_LABEL = {
  homework:            'Only if there are overdue or due-today items',
  attendance:          'Only if any employee is absent today',
  toddleLessonPlan:    'Only if there are pending or missing lesson plans',
  morningActivity:     'Only if there are pending / missed activities today',
  learningTrackers:    'Only if there are pending tracker submissions',
  newsletter:          'Only if there are draft newsletters',
  grievances:          'Only if there are open grievances',
  teacherObservations: 'Only if there are pending observations',
};

// Modules that support per-person personalized responsible messages
const SUPPORTS_RESPONSIBLE = new Set([
  'homework', 'toddleLessonPlan', 'morningActivity',
  'learningTrackers', 'grievances', 'teacherObservations', 'newsletter',
]);

function parseDays(str) {
  if (!str) return [1, 2, 3, 4, 5];
  return str.split(',').map(d => parseInt(d.trim(), 10)).filter(n => !isNaN(n));
}
function joinDays(arr) {
  return arr.slice().sort((a, b) => a - b).join(',');
}
function fmtDayLabels(str) {
  const nums = parseDays(str);
  return ALL_DAYS.filter(d => nums.includes(d.value)).map(d => d.label).join(', ') || '—';
}
function fmtDatetime(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function defaultRecipients() {
  return { global_chat: true, roles: [], employees: [], responsible: false };
}
function initRecipients(rule) {
  if (rule?.recipients && typeof rule.recipients === 'object') return rule.recipients;
  return defaultRecipients();
}
function recipientSummary(recips) {
  if (!recips) return 'Global Chat';
  const parts = [];
  if (recips.global_chat) parts.push('Global Chat');
  if (recips.roles?.length) parts.push(...recips.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)));
  if (recips.employees?.length) parts.push(`${recips.employees.length} person${recips.employees.length > 1 ? 's' : ''}`);
  if (recips.responsible) parts.push('Responsible');
  return parts.length ? parts.join(' · ') : 'None configured';
}
function conditionBadge(cfg) {
  if (!cfg.condition || cfg.condition === 'always') return null;
  if (cfg.condition === 'if_pending') return 'If pending';
  if (cfg.condition === 'if_below_pct') return `If < ${cfg.condition_value || 80}%`;
  return null;
}

// ── Recipients selector ───────────────────────────────────────
function RecipientsSection({ module, value, onChange, staffWithTelegram, availableRoles }) {
  const [empSearch, setEmpSearch] = useState('');
  const set = (key, val) => onChange({ ...value, [key]: val });

  const toggleRole = role => {
    const roles = value.roles || [];
    set('roles', roles.includes(role) ? roles.filter(r => r !== role) : [...roles, role]);
  };
  const addEmployee = emp => {
    const emps = value.employees || [];
    if (emps.find(e => e.id === emp.id)) return;
    set('employees', [...emps, { id: emp.id, name: emp.name }]);
    setEmpSearch('');
  };
  const removeEmployee = id => set('employees', (value.employees || []).filter(e => e.id !== id));

  const filteredStaff = staffWithTelegram.filter(e =>
    e.name.toLowerCase().includes(empSearch.toLowerCase()) &&
    !(value.employees || []).find(s => s.id === e.id)
  );

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-2">
        Recipients
        <span className="text-slate-400 font-normal ml-1">— who gets this notification</span>
      </label>
      <div className="border border-slate-200 rounded-xl p-3 space-y-3 bg-slate-50/40">

        {/* Global Management Chat */}
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={!!value.global_chat}
            onChange={e => set('global_chat', e.target.checked)}
            className="mt-0.5 w-3.5 h-3.5 rounded accent-blue-600" />
          <span className="text-xs text-slate-700">
            <span className="font-medium">Management Chat</span>
            <span className="text-slate-400 ml-1">(global Telegram channel)</span>
          </span>
        </label>

        {/* Responsible Person */}
        {SUPPORTS_RESPONSIBLE.has(module) && (
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={!!value.responsible}
              onChange={e => set('responsible', e.target.checked)}
              className="mt-0.5 w-3.5 h-3.5 rounded accent-violet-600" />
            <span className="text-xs text-slate-700">
              <span className="font-medium text-violet-700">Responsible Person</span>
              <span className="text-slate-400 ml-1">— personalized message per responsible employee</span>
            </span>
          </label>
        )}

        {/* By Role */}
        {availableRoles.length > 0 && (
          <div>
            <p className="text-[11px] font-medium text-slate-500 mb-1.5">By Role:</p>
            <div className="flex flex-wrap gap-1.5">
              {availableRoles.map(role => {
                const selected = (value.roles || []).includes(role);
                return (
                  <button key={role} type="button" onClick={() => toggleRole(role)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      selected
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-emerald-300'
                    }`}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Specific Employees */}
        <div>
          <p className="text-[11px] font-medium text-slate-500 mb-1.5">
            Specific People:
            <span className="text-slate-400 font-normal ml-1">must have Telegram set up</span>
          </p>
          {(value.employees || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(value.employees || []).map(emp => (
                <span key={emp.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-medium">
                  <User size={9} />{emp.name}
                  <button type="button" onClick={() => removeEmployee(emp.id)} className="hover:text-blue-900 ml-0.5">
                    <X size={9} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="relative">
            <input value={empSearch} onChange={e => setEmpSearch(e.target.value)}
              placeholder={staffWithTelegram.length ? 'Search by name…' : 'No employees with Telegram set up'}
              disabled={!staffWithTelegram.length}
              className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white disabled:bg-slate-50 disabled:text-slate-400" />
            {empSearch.length > 0 && (
              <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                {filteredStaff.length === 0
                  ? <p className="px-3 py-2 text-xs text-slate-400">No match</p>
                  : filteredStaff.slice(0, 8).map(e => (
                    <button key={e.id} type="button" onClick={() => addEmployee(e)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center justify-between gap-2">
                      <span className="font-medium text-slate-700">{e.name}</span>
                      {e.role_name && (
                        <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
                          {e.role_name}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Condition / rule selector ─────────────────────────────────
function ConditionSection({ module, condition, conditionValue, onConditionChange, onValueChange }) {
  const available = MODULE_CONDITIONS[module] || ['always', 'if_pending'];

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-2">
        <span className="inline-flex items-center gap-1">
          <Filter size={11} className="text-slate-500" />
          Send Rule (Condition)
        </span>
        <span className="text-slate-400 font-normal ml-1">— when should this notification fire?</span>
      </label>
      <div className="border border-slate-200 rounded-xl p-3 space-y-2.5 bg-slate-50/40">
        {available.includes('always') && (
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="radio" name="condition" value="always"
              checked={condition === 'always'}
              onChange={() => onConditionChange('always')}
              className="w-3.5 h-3.5 accent-blue-600" />
            <span className="text-xs text-slate-700">
              <span className="font-medium">Always</span>
              <span className="text-slate-400 ml-1">— send every scheduled time regardless</span>
            </span>
          </label>
        )}

        {available.includes('if_pending') && (
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="radio" name="condition" value="if_pending"
              checked={condition === 'if_pending'}
              onChange={() => onConditionChange('if_pending')}
              className="w-3.5 h-3.5 accent-amber-500" />
            <span className="text-xs text-slate-700">
              <span className="font-medium text-amber-700">Conditional</span>
              <span className="text-slate-400 ml-1">
                — {PENDING_LABEL[module] || 'Only if there are pending items'}
              </span>
            </span>
          </label>
        )}

        {available.includes('if_below_pct') && (
          <div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="radio" name="condition" value="if_below_pct"
                checked={condition === 'if_below_pct'}
                onChange={() => onConditionChange('if_below_pct')}
                className="w-3.5 h-3.5 accent-red-500" />
              <span className="text-xs text-slate-700">
                <span className="font-medium text-red-700">Threshold</span>
                <span className="text-slate-400 ml-1">— only if completion rate is below</span>
              </span>
            </label>
            {condition === 'if_below_pct' && (
              <div className="flex items-center gap-2 mt-2 ml-6">
                <input
                  type="number" min={1} max={99}
                  value={conditionValue}
                  onChange={e => onValueChange(Math.min(99, Math.max(1, parseInt(e.target.value) || 80)))}
                  className="w-16 border border-red-200 rounded-lg px-2 py-1 text-xs text-center font-semibold focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                />
                <span className="text-xs text-slate-500 font-medium">%</span>
                <span className="text-[11px] text-slate-400">
                  (send only when below {conditionValue}%)
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Single rule form (add / edit) ────────────────────────────
function RuleForm({ initial, module, onSave, onCancel, saving, staffWithTelegram, availableRoles }) {
  const [form, setForm] = useState(() => ({
    rule_name:       initial?.rule_name       || '',
    send_time:       initial?.send_time       || '08:00',
    days:            parseDays(initial?.days_of_week),
    is_enabled:      initial?.is_enabled      !== false,
    recipients:      initRecipients(initial),
    condition:       initial?.condition       || 'always',
    condition_value: initial?.condition_value || 80,
  }));

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleDay = v =>
    set('days', form.days.includes(v) ? form.days.filter(d => d !== v) : [...form.days, v]);

  const handleSubmit = e => {
    e.preventDefault();
    if (!form.rule_name.trim()) { toast.error('Name is required'); return; }
    if (!form.days.length)      { toast.error('Select at least one day'); return; }
    const r = form.recipients;
    if (!r.global_chat && !r.responsible && !r.roles?.length && !r.employees?.length) {
      toast.error('Select at least one recipient');
      return;
    }
    onSave({
      rule_name:       form.rule_name.trim(),
      send_time:       form.send_time,
      days_of_week:    joinDays(form.days),
      is_enabled:      form.is_enabled,
      recipients:      form.recipients,
      condition:       form.condition,
      condition_value: form.condition === 'if_below_pct' ? form.condition_value : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-4 border-t border-slate-100">
      {/* Name + Time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">
            Rule Name <span className="text-red-400">*</span>
          </label>
          <input value={form.rule_name} onChange={e => set('rule_name', e.target.value)}
            placeholder="e.g. Evening Homework Alert"
            required
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          <p className="text-[10px] text-slate-400 mt-1">Included in every notification message</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1.5">Send Time</label>
          <input type="time" value={form.send_time} onChange={e => set('send_time', e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
      </div>

      {/* Days */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-2">Days</label>
        <div className="flex flex-wrap gap-2">
          {ALL_DAYS.map(d => (
            <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                form.days.includes(d.value)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'
              }`}>
              {d.label}
            </button>
          ))}
          <button type="button" onClick={() => set('days', [1,2,3,4,5])}
            className="px-2.5 py-1.5 rounded-lg text-[11px] text-slate-400 border border-dashed border-slate-200 hover:border-slate-300">
            Mon–Fri
          </button>
          <button type="button" onClick={() => set('days', [0,1,2,3,4,5,6])}
            className="px-2.5 py-1.5 rounded-lg text-[11px] text-slate-400 border border-dashed border-slate-200 hover:border-slate-300">
            Every day
          </button>
        </div>
      </div>

      {/* Send Rule / Condition */}
      <ConditionSection
        module={module}
        condition={form.condition}
        conditionValue={form.condition_value}
        onConditionChange={v => set('condition', v)}
        onValueChange={v => set('condition_value', v)}
      />

      {/* Recipients */}
      <RecipientsSection
        module={module}
        value={form.recipients}
        onChange={v => set('recipients', v)}
        staffWithTelegram={staffWithTelegram}
        availableRoles={availableRoles}
      />

      {/* Enabled toggle */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => set('is_enabled', !form.is_enabled)}
          className="flex items-center gap-1.5 text-sm">
          {form.is_enabled
            ? <ToggleRight size={20} className="text-emerald-600" />
            : <ToggleLeft  size={20} className="text-slate-400"   />}
          <span className={form.is_enabled ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
            {form.is_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </button>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-60 transition-colors">
          {saving ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle size={13} />}
          {saving ? 'Saving…' : 'Save Rule'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-lg hover:bg-slate-50 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Single rule card ──────────────────────────────────────────
function RuleCard({ rule, onToggle, onEdit, onDelete, onTest, onViewLogs }) {
  const [deleting, setDeleting] = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${rule.rule_name}"?`)) return;
    setDeleting(true);
    try { await onDelete(rule.id); } finally { setDeleting(false); }
  };
  const handleTest = async () => {
    setTesting(true);
    try { await onTest(rule.id); } finally { setTesting(false); }
  };
  const handleToggle = async () => {
    setToggling(true);
    try { await onToggle(rule.id); } finally { setToggling(false); }
  };

  const badge = conditionBadge(rule);

  return (
    <div className={`border rounded-xl p-4 transition-colors ${
      rule.is_enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-70'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${rule.is_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{rule.rule_name}</span>
            <span className="text-xs text-slate-400">{rule.send_time} · {fmtDayLabels(rule.days_of_week)}</span>
            {badge && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                rule.condition === 'if_below_pct'
                  ? 'bg-red-50 text-red-600 border border-red-200'
                  : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                {badge}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <Users size={10} className="text-slate-400 flex-shrink-0" />
            <span className="text-[11px] text-slate-400">{recipientSummary(rule.recipients)}</span>
          </div>
          {rule.last_sent_at && (
            <p className="text-[11px] text-slate-400 mt-0.5">Last sent: {fmtDatetime(rule.last_sent_at)}</p>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={handleTest} disabled={testing} title="Send test now"
            className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-50">
            {testing ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
          <button onClick={() => onViewLogs(rule.id)} title="View logs"
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <ChevronDown size={14} />
          </button>
          <button onClick={() => onEdit(rule)} title="Edit"
            className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors">
            <Edit2 size={14} />
          </button>
          <button onClick={handleToggle} disabled={toggling} title={rule.is_enabled ? 'Disable' : 'Enable'}
            className="p-1.5 rounded-lg transition-colors text-slate-400 hover:text-slate-600">
            {rule.is_enabled
              ? <ToggleRight size={16} className="text-emerald-500" />
              : <ToggleLeft  size={16} />}
          </button>
          <button onClick={handleDelete} disabled={deleting} title="Delete"
            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50">
            {deleting ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Logs panel ────────────────────────────────────────────────
function LogsPanel({ ruleId, module, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    telegramNotifyAPI.getLogs({ rule_id: ruleId, module, limit: 10 })
      .then(r => setLogs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ruleId, module]);

  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden mt-2">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Send Log</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
          <X size={13} />
        </button>
      </div>
      {loading ? (
        <p className="px-4 py-4 text-center text-xs text-slate-400">Loading…</p>
      ) : logs.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-slate-400">No sends yet</p>
      ) : (
        <div className="divide-y divide-slate-50 max-h-52 overflow-y-auto">
          {logs.map(log => (
            <div key={log.id} className="px-4 py-2.5 flex items-start gap-2.5">
              {log.status === 'success'
                ? <CheckCircle size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                : <XCircle    size={13} className="text-red-400 mt-0.5 flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    log.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                  }`}>{log.status}</span>
                  <span className="text-[11px] text-slate-400">{fmtDatetime(log.sent_at)}</span>
                </div>
                {log.status === 'error' && log.error_message && (
                  <p className="text-xs text-red-500 mt-0.5 truncate">{log.error_message}</p>
                )}
                {log.status === 'success' && log.message_preview && (
                  <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2 whitespace-pre-line">
                    {log.message_preview}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function TelegramNotificationSetup({ module, moduleLabel, yearId }) {
  const [rules,             setRules]             = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [adding,            setAdding]            = useState(false);
  const [editRule,          setEditRule]          = useState(null);
  const [savingId,          setSavingId]          = useState(null);
  const [logsFor,           setLogsFor]           = useState(null);
  const [staffWithTelegram, setStaffWithTelegram] = useState([]);
  const [availableRoles,    setAvailableRoles]    = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await telegramNotifyAPI.getConfigs({ module, academic_year_id: yearId });
      setRules(data);
    } catch { toast.error('Failed to load Telegram notifications'); }
    finally { setLoading(false); }
  }, [module, yearId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    telegramNotifyAPI.getStaff().then(r => setStaffWithTelegram(r.data)).catch(() => {});
    telegramNotifyAPI.getRoles().then(r => setAvailableRoles(r.data)).catch(() => {});
  }, []);

  const handleAdd = async (formData) => {
    setSavingId('new');
    try {
      await telegramNotifyAPI.createConfig({ module, academic_year_id: yearId, ...formData });
      toast.success('Rule added');
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to add');
    } finally { setSavingId(null); }
  };

  const handleUpdate = async (formData) => {
    setSavingId(editRule.id);
    try {
      await telegramNotifyAPI.updateConfig(editRule.id, formData);
      toast.success('Rule updated');
      setEditRule(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update');
    } finally { setSavingId(null); }
  };

  const handleDelete = async (id) => {
    await telegramNotifyAPI.deleteConfig(id);
    toast.success('Deleted');
    if (logsFor === id) setLogsFor(null);
    load();
  };

  const handleToggle = async (id) => {
    const { data } = await telegramNotifyAPI.toggleConfig(id);
    setRules(prev => prev.map(r => r.id === id ? { ...r, is_enabled: data.is_enabled } : r));
  };

  const handleTest = async (id) => {
    try {
      const { data } = await telegramNotifyAPI.testSend(id);
      const count = data.sentTo?.length || 1;
      toast.success(`Test sent to ${count} recipient${count > 1 ? 's' : ''}!`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Test send failed');
    }
  };

  const formProps = { module, staffWithTelegram, availableRoles };

  return (
    <div className="glass-card p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Send size={16} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-700">Telegram Notifications</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Sends: {MODULE_INFO[module] || `${moduleLabel} summary`}
            </p>
          </div>
        </div>
        {!adding && !editRule && (
          <button onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
            <Plus size={13} /> Add Rule
          </button>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <RuleForm {...formProps}
          onSave={handleAdd}
          onCancel={() => setAdding(false)}
          saving={savingId === 'new'}
        />
      )}

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[1,2].map(i => (
            <div key={i} className="border border-slate-100 rounded-xl p-4 animate-pulse">
              <div className="h-3 bg-slate-100 rounded w-40 mb-2" />
              <div className="h-2 bg-slate-50 rounded w-56" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && rules.length === 0 && !adding && (
        <div className="border border-dashed border-slate-200 rounded-xl p-6 text-center">
          <Send size={24} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-400">No notification rules yet.</p>
          <p className="text-xs text-slate-300 mt-0.5">Click "Add Rule" to create your first one.</p>
        </div>
      )}

      {/* Rules list */}
      {!loading && rules.map(rule => (
        <div key={rule.id}>
          {editRule?.id === rule.id ? (
            <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-blue-700">Editing: {rule.rule_name}</span>
                <button onClick={() => setEditRule(null)} className="text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              </div>
              <RuleForm {...formProps}
                initial={rule}
                onSave={handleUpdate}
                onCancel={() => setEditRule(null)}
                saving={savingId === rule.id}
              />
            </div>
          ) : (
            <RuleCard
              rule={rule}
              onToggle={handleToggle}
              onEdit={r => { setEditRule(r); setAdding(false); }}
              onDelete={handleDelete}
              onTest={handleTest}
              onViewLogs={id => setLogsFor(logsFor === id ? null : id)}
            />
          )}

          {logsFor === rule.id && editRule?.id !== rule.id && (
            <LogsPanel ruleId={rule.id} module={module} onClose={() => setLogsFor(null)} />
          )}
        </div>
      ))}
    </div>
  );
}
